import { useState, useEffect } from "react";
import { getAttachmentUrl, isValidAttachmentKey } from "@utils/attachment.utils";

export interface UseAttachmentReturn {
	url: string;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * React hook for fetching attachment URLs
 * @param key - The attachment key from backend
 * @returns UseAttachmentReturn - Object containing URL, loading state, error, and refetch function
 */
export const useAttachment = (key: string | null | undefined): UseAttachmentReturn => {
	const [url, setUrl] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchAttachment = async () => {
		if (!isValidAttachmentKey(key)) {
			setUrl("");
			setError(null);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const result = await getAttachmentUrl(key!);
			if (result.success) {
				setUrl(result.url);
			} else {
				setError(result.message || "Failed to fetch attachment");
				setUrl("");
			}
		} catch (err) {
			console.log(err);
			setError("Failed to fetch attachment");
			setUrl("");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAttachment();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);

	return {
		url,
		loading,
		error,
		refetch: fetchAttachment,
	};
};
